if (!customElements.get('gift-box-selector')) {
  customElements.define(
    'gift-box-selector',
    class GiftBoxSelector extends HTMLElement {
      constructor() {
        super();
        this.onChange = this.onChange.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.configError = this.hasAttribute('data-config-error');

        // Liquid側で検証済みの option_value.id / variant.id（銀だら0〜3個に対応）。欠けていれば fail closed。
        this.optionValueIds = (this.dataset.optionValueIds || '').split(',');
        this.variantIds = (this.dataset.variantIds || '').split(',');
        if (!this.configError) {
          const idsOk =
            this.optionValueIds.length === 4 &&
            this.optionValueIds.every(Boolean) &&
            this.variantIds.length === 4 &&
            this.variantIds.every(Boolean);
          // 各グループに追加料金オプション（data-fish-key）がちょうど1つ無ければ、
          // 加算カウントが狂ったまま安いバリアントで通ってしまう（fail open）ため、ここで塞ぐ
          const fields = this.fields;
          const fieldsOk =
            fields.length === 3 &&
            fields.every((field) => field.querySelectorAll('option[data-fish-key]').length === 1);
          if (!idsOk || !fieldsOk) {
            this.setConfigError('data属性またはセレクト構造が不正です');
          }
        }

        this.addEventListener('change', this.onChange);
        document.addEventListener('submit', this.onSubmitCapture, true);

        // Dawn側（売り切れ・購入不可）の最終状態。true の間は自ロック解除でボタンを有効化しない
        const button = this.submitButton;
        this.dawnDisabled = !button || button.hasAttribute('disabled');

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        // 復元されたフォーム状態や ?variant= 付きURLの初期表示を、現在の選択内容に揃える
        this.syncVariant();
        this.applyButtonState();

        // スニペット内のインライン読み込み前ガードに「本スクリプト稼働中」を伝える
        this.dataset.jsReady = 'true';
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        document.removeEventListener('submit', this.onSubmitCapture, true);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      setConfigError(reason) {
        this.configError = true;
        this.setAttribute('data-config-error', '');
        if (reason) console.error(`[gift-box-selector] 設定エラーによりカート追加を無効化: ${reason}`);
      }

      // クイック追加モーダル内では自要素の data-section-id が quickadd- 接頭辞付きに書き換わる一方、
      // product-info は元のセクションIDで variantChange を publish する
      matchesSection(publishedSectionId) {
        if (!publishedSectionId) return false;
        return publishedSectionId === this.sectionId || this.sectionId === `quickadd-${publishedSectionId}`;
      }

      onVariantChange(event) {
        const data = event && event.data;
        if (!data || !this.matchesSection(data.sectionId)) return;
        // レンダリング済みHTML内のボタンIDは publish 側のセクションIDに揃う
        const renderedButton = data.html && data.html.getElementById(`ProductSubmitButton-${data.sectionId}`);
        this.dawnDisabled = !renderedButton || renderedButton.hasAttribute('disabled');
        this.applyButtonState();
      }

      // product-info の再レンダリングで参照が古くなるため、毎回取得する
      get form() {
        return document.getElementById(this.productFormId);
      }

      get submitButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      get variantInput() {
        const form = this.form;
        return form ? form.querySelector('input[name="id"]') : null;
      }

      get messageElement() {
        return this.querySelector('.gift-box-selector__message');
      }

      get fields() {
        return Array.from(this.querySelectorAll('select[data-gift-box-field]'));
      }

      get selectionComplete() {
        const fields = this.fields;
        return fields.length === 3 && fields.every((field) => field.value !== '');
      }

      get premiumCount() {
        return this.fields.filter((field) => {
          const selected = field.selectedOptions && field.selectedOptions[0];
          return selected && selected.hasAttribute('data-fish-key');
        }).length;
      }

      get expectedVariantId() {
        return this.variantIds[this.premiumCount];
      }

      // フォームの input[name=id] が現在の選択に対応するバリアントを指しているか。
      // バリアント切替フェッチ中の古いID・復元されたフォーム状態での不整合を、有効化と送信の両方で弾く
      get variantConsistent() {
        const input = this.variantInput;
        return !!input && input.value === this.expectedVariantId;
      }

      onChange(event) {
        if (!(event.target instanceof HTMLSelectElement) || !event.target.hasAttribute('data-gift-box-field')) return;

        // 先にボタン状態を確定させてからバリアントを切り替える。
        // 逆順にすると、product-info のフェッチ中ロックを直後に解除してしまう競合が起きる。
        this.applyButtonState();
        if (this.selectionComplete) this.hideMessage();

        this.syncVariant();
        if (this.configError) this.applyButtonState();
      }

      syncVariant() {
        if (this.configError) return;

        const targetOptionValueId = this.optionValueIds[this.premiumCount];
        const variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
        const option = variantSelects
          ? variantSelects.querySelector(`option[data-option-value-id="${targetOptionValueId}"]`)
          : null;
        const select = option ? option.closest('select') : null;

        if (!option || !select) {
          // 隠しバリアントピッカーが見つからない場合は購入不可に固定する（fail closed）
          this.setConfigError('バリアントピッカー（ドロップダウン型）が見つかりません');
          return;
        }

        if (select.value === option.value) {
          // フェッチ失敗等で input[name=id] が未更新のまま残った場合、同値でも再dispatchして復帰経路を残す
          if (!this.variantConsistent) select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      applyButtonState() {
        const button = this.submitButton;
        if (!button) return;

        const buttonText = button.querySelector('span');
        const lockedByUs = button.hasAttribute('data-gift-box-disabled');

        if (this.dawnDisabled) {
          // 売り切れ・購入不可はDawn側の状態を優先し、自ロックの痕跡だけ取り除く
          if (lockedByUs) {
            button.removeAttribute('data-gift-box-disabled');
            if (buttonText && window.variantStrings && window.variantStrings.soldOut) {
              buttonText.textContent = window.variantStrings.soldOut;
            }
          }
          button.setAttribute('disabled', 'disabled');
          return;
        }

        const shouldLock = this.configError || !this.selectionComplete || !this.variantConsistent;
        if (shouldLock) {
          // フェッチ中のDawn一時disabledには触れない（自ロックが既に付いている場合のみ更新）
          if (button.hasAttribute('disabled') && !lockedByUs) return;
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('data-gift-box-disabled', '');
          const lockMessage = this.configError ? this.dataset.unavailableMessage : this.dataset.incompleteMessage;
          if (buttonText && lockMessage) {
            buttonText.textContent = lockMessage;
          }
        } else if (lockedByUs) {
          // 自分が掛けたロックだけを解除する
          button.removeAttribute('data-gift-box-disabled');
          button.removeAttribute('disabled');
          if (buttonText) {
            buttonText.textContent = (window.variantStrings && window.variantStrings.addToCart) || buttonText.textContent;
          }
        }
      }

      onSubmitCapture(event) {
        if (event.target !== this.form) return;
        if (this.configError || !this.selectionComplete || !this.variantConsistent) {
          event.preventDefault();
          event.stopPropagation();
          this.showMessage();
        }
      }

      showMessage() {
        const message = this.messageElement;
        if (message) message.removeAttribute('hidden');
      }

      hideMessage() {
        const message = this.messageElement;
        if (message) message.setAttribute('hidden', '');
      }
    }
  );
}
